/**
 * Routes Inventory Panel Configuration
 *
 * @version 1.0
 * @updated 2026-02-27
 * @module core/modules/waste/config
 *
 * @description
 * Configuración del panel de inventario de rutas para BaseInventoryPanel.
 * Define columnas, formatters, eventos y comportamientos específicos.
 */

import { useRoutes } from '../hooks/useRoutes';
import { formatDistance } from '../utils/route-geometry-utils';
import { COLLECTION_FREQUENCIES } from '../constants';
import type { WasteRoute } from '../types';
import type {
  InventoryPanelConfig,
  InventoryRow,
} from '../../../layouts/panels/LeftPanel';

// ─────────────────────────────────────────
// Formatters
// ─────────────────────────────────────────

/**
 * Traduce el valor de frecuencia a texto legible.
 */
function translateFrequency(v: string | null): string {
  if (!v) return '—';
  const freq = COLLECTION_FREQUENCIES.find(f => f.value === v);
  return freq?.label ?? v;
}

/**
 * Formatea la distancia en metros a texto legible.
 */
function formatDistanceValue(meters: number | null): string {
  if (meters === null || meters === undefined) return '—';
  return formatDistance(meters);
}

// ─────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────

/**
 * Configuración completa del panel de inventario de rutas.
 */
export const routesInventoryConfig: InventoryPanelConfig<WasteRoute> = {
  // ── Identidad ──
  entityType: 'route',
  displayName: 'Rutas',
  displayNameSingular: 'ruta',

  // ── Columnas narrow (sidebar colapsado) ──
  narrowColumns: [
    {
      key: 'code',
      label: 'Código',
      title: 'Código de la ruta',
      width: '70px'
    },
    {
      key: 'name',
      label: 'Nombre',
      title: 'Nombre de la ruta',
      width: '1fr'
    },
  ],

  // ── Columnas wide (sidebar expandido) ──
  wideColumns: [
    {
      key: 'code',
      label: 'Cód',
      title: 'Código de la ruta',
      width: '70px'
    },
    {
      key: 'name',
      label: 'Nombre',
      title: 'Nombre de la ruta',
      width: '1.5fr'
    },
    {
      key: 'frequency',
      label: 'Frecuencia',
      title: 'Frecuencia de recolección',
      width: '90px',
      format: translateFrequency,
    },
    {
      key: 'distance',
      label: 'Distancia',
      title: 'Distancia estimada',
      width: '75px',
      format: formatDistanceValue,
    },
    {
      key: 'containerCount',
      label: 'Contenedores',
      title: 'Número de contenedores',
      width: '85px'
    },
  ],

  // ── Data fetching ──
  useData: (projectId: string) => {
    const { routes, isLoading, error, deleteRoute, loadRoutes } = useRoutes({
      projectId,
      autoLoad: true,
    });

    return {
      data: routes,
      isLoading,
      error,
      reload: loadRoutes,
      delete: deleteRoute,
    };
  },

  // ── Row builder ──
  buildRow: (route: WasteRoute): InventoryRow => ({
    id: route.id,
    code: route.code,
    name: route.name,
    frequency: route.frequency,
    distance: route.estimatedDistance,
    containerCount: route.containerIds?.length ?? 0,
  }),

  // ── Map integration ──
  events: {
    select: 'waste-route-select',
    visibilityToggle: 'waste-route-visibility-toggle',
    hover: 'waste-route-hover',
    zoom: 'waste-route-zoom',
    created: 'route-created',
    updated: 'route-updated',
    deleted: 'route-deleted',
  },

  getZoomTarget: (route: WasteRoute) => ({
    type: 'line',
    coordinates: route.geometry?.coordinates ?? [],
  }),

  // ── Panel derecho ──
  detailsPanelType: 'route',

  // ── Módulo ──
  moduleId: 'waste',
};
